/**
 * Agents redesign — typed fixtures (UI track, Phase 1).
 *
 * These mirror the eventual API contracts (agents, agent_runs, agent_approvals,
 * agent_grants) closely enough that swapping the fixture-backed client for the live
 * endpoints needs no component changes. They render every Phase-1 state: active/paused
 * agents, an agent with a pending sign-off, reversibility-tagged actions, recent runs
 * (approved / auto-trusted), and a "just updated from chat" condition.
 */

export type Autonomy = "suggest" | "confirm" | "auto";
export type TriggerMode = "one-shot" | "scheduled" | "reactive";
export type AgentStatus = "active" | "paused";
export type Reversibility = "free" | "sign-off";

export interface AgentAction {
  id: string;
  label: string;
  reversibility: Reversibility;
  /** In-context grant note, e.g. "Send asks each time — unless the recipient is a Contact you already know (grant active)". */
  grantNote?: string;
}

export interface AgentBuild {
  /** Plain-language trigger, with an optional highlighted token (e.g. a tag/Contact). */
  trigger: string;
  triggerToken?: string;
  condition?: string;
  conditionJustUpdated?: boolean;
  actions: AgentAction[];
  guardrails: { neverNewDomains: boolean; flagUntrusted: boolean };
}

export interface AgentRunLite {
  id: string;
  /** Short relative label, e.g. "Mon 4:01". */
  when: string;
  /** What it did, e.g. "replied to Priya N.". */
  summary: string;
  status: "approved" | "auto" | "pending" | "rejected" | "skipped";
  /** Auto runs that fired under a standing grant read "auto · trusted". */
  trusted?: boolean;
}

export interface PendingWork {
  /** e.g. "1 draft to Dana W. awaiting sign-off". */
  label: string;
  approvalId: string;
}

export interface Agent {
  id: string;
  name: string;
  role: string;
  /** Per-agent identity color — shared by the status dot, header, and chat scope chip. */
  color: string;
  status: AgentStatus;
  autonomy: Autonomy;
  triggerMode: TriggerMode;
  pendingCount: number;
  lastRunLabel?: string;
  nextRunLabel?: string;
  recentActivity?: string;
  build: AgentBuild;
  pending?: PendingWork;
  runs: AgentRunLite[];
}

/**
 * A live in-flight run shown in the Agents tab's "Running now" strip — a one-off task or a
 * routine execution currently working. Fixtures for now (the durable run engine isn't wired);
 * this mirrors the eventual live run-status feed so the strip flips to live with no UI change.
 */
export interface RunningRun {
  id: string;
  title: string;
  /** Progress sub-line, e.g. "emailed · waiting on reply" or "gathering sources · 6 of ~12". */
  sub: string;
  /** Short state pill, e.g. "negotiating" | "working". */
  state: string;
  /** The agent this run belongs to — clicking the row opens that agent's card (live data only). */
  agentId?: string;
}

export const runningNow: RunningRun[] = [
  {
    id: "rn-1",
    title: "Boulevard reservation",
    sub: "emailed · waiting on reply",
    state: "negotiating",
  },
  {
    id: "rn-2",
    title: "Saylor BTC report",
    sub: "gathering sources · 6 of ~12",
    state: "working",
  },
];

const PALETTE = {
  amber: "#b4763f",
  rose: "#a8546a",
  green: "#4f8a6b",
  violet: "#7a5ea8",
  slate: "#7a7f85",
};

export const FIXTURE_AGENTS: Agent[] = [
  {
    id: "a-morning-brief",
    name: "Morning brief",
    role: "Builds your 7am day-at-a-glance",
    color: PALETTE.amber,
    status: "active",
    autonomy: "auto",
    triggerMode: "scheduled",
    pendingCount: 0,
    lastRunLabel: "Today 7:00",
    nextRunLabel: "Tomorrow 7:00",
    recentActivity: "Posted today’s brief — 3 meetings, 1 needs prep.",
    build: {
      trigger: "every day at 7:00",
      condition: undefined,
      actions: [
        { id: "ac1", label: "Read calendar + inbox", reversibility: "free" },
        {
          id: "ac2",
          label: "Write the brief to your feed",
          reversibility: "free",
        },
      ],
      guardrails: { neverNewDomains: true, flagUntrusted: true },
    },
    runs: [
      {
        id: "r1",
        when: "Today 7:00",
        summary: "posted the morning brief",
        status: "auto",
        trusted: true,
      },
      {
        id: "r2",
        when: "Yesterday 7:00",
        summary: "posted the morning brief",
        status: "auto",
        trusted: true,
      },
    ],
  },
  {
    id: "a-follow-up",
    name: "Follow-up chaser",
    role: "Drafts replies to investors who ask to meet",
    color: PALETTE.rose,
    status: "active",
    autonomy: "confirm",
    triggerMode: "reactive",
    pendingCount: 1,
    lastRunLabel: "Mon 4:01",
    recentActivity: "1 draft awaiting your sign-off.",
    build: {
      trigger: "new email from a Contact tagged",
      triggerToken: "investor",
      condition: "email asks to meet",
      conditionJustUpdated: true,
      actions: [
        {
          id: "fa1",
          label: "Read calendar availability",
          reversibility: "free",
        },
        {
          id: "fa2",
          label: "Draft reply with 3 proposed slots",
          reversibility: "free",
        },
        {
          id: "fa3",
          label: "Send the email",
          reversibility: "sign-off",
          grantNote:
            "Send asks each time — unless the recipient is a Contact you already know (grant active).",
        },
      ],
      guardrails: { neverNewDomains: true, flagUntrusted: true },
    },
    pending: {
      label: "1 draft to Dana W. awaiting sign-off",
      approvalId: "ap-dana",
    },
    runs: [
      {
        id: "fr1",
        when: "Mon 4:01",
        summary: "replied to Priya N.",
        status: "approved",
      },
      {
        id: "fr2",
        when: "Fri 11:20",
        summary: "replied to Marcus H.",
        status: "auto",
        trusted: true,
      },
    ],
  },
  {
    id: "a-inbox-triager",
    name: "Inbox triager",
    role: "Sorts and labels new mail as it lands",
    color: PALETTE.green,
    status: "active",
    autonomy: "auto",
    triggerMode: "reactive",
    pendingCount: 0,
    lastRunLabel: "12m ago",
    recentActivity: "Labelled 6 threads, archived 2 newsletters.",
    build: {
      trigger: "new email arrives",
      actions: [
        { id: "it1", label: "Classify the thread", reversibility: "free" },
        { id: "it2", label: "Apply a label", reversibility: "free" },
      ],
      guardrails: { neverNewDomains: true, flagUntrusted: true },
    },
    runs: [
      {
        id: "ir1",
        when: "12m ago",
        summary: "labelled 6 threads",
        status: "auto",
        trusted: true,
      },
    ],
  },
  {
    id: "a-weekly-digest",
    name: "Weekly digest",
    role: "Summarises your week every Friday",
    color: PALETTE.violet,
    status: "active",
    autonomy: "confirm",
    triggerMode: "scheduled",
    pendingCount: 0,
    lastRunLabel: "Fri 16:00",
    nextRunLabel: "Fri 16:00",
    recentActivity: "Drafted last Friday’s digest — you approved it.",
    build: {
      trigger: "every Friday at 16:00",
      actions: [
        {
          id: "wd1",
          label: "Gather the week’s activity",
          reversibility: "free",
        },
        { id: "wd2", label: "Draft the digest", reversibility: "free" },
        { id: "wd3", label: "Send to your team", reversibility: "sign-off" },
      ],
      guardrails: { neverNewDomains: true, flagUntrusted: true },
    },
    runs: [
      {
        id: "wr1",
        when: "Fri 16:00",
        summary: "drafted the weekly digest",
        status: "approved",
      },
    ],
  },
  {
    id: "a-meeting-prep",
    name: "Meeting prep",
    role: "Briefs you before every meeting",
    color: PALETTE.slate,
    status: "paused",
    autonomy: "suggest",
    triggerMode: "reactive",
    pendingCount: 0,
    lastRunLabel: "3 days ago",
    recentActivity: "Paused.",
    build: {
      trigger: "30 minutes before a calendar event",
      actions: [
        { id: "mp1", label: "Pull attendee context", reversibility: "free" },
        { id: "mp2", label: "Draft a prep note", reversibility: "free" },
      ],
      guardrails: { neverNewDomains: true, flagUntrusted: true },
    },
    runs: [
      {
        id: "mr1",
        when: "3 days ago",
        summary: "prepped your 1:1 with Sam",
        status: "approved",
      },
    ],
  },
];

/** Templates that drop a pre-filled recipe into Build (Phase 2 wires the recipe). */
export const FIXTURE_TEMPLATES = [
  {
    id: "t-daily-brief",
    name: "Daily brief",
    blurb: "A 7am day-at-a-glance in your feed.",
  },
  {
    id: "t-follow-up",
    name: "Follow-up chaser",
    blurb: "Drafts replies when someone asks to meet.",
  },
  {
    id: "t-inbox-triager",
    name: "Inbox triager",
    blurb: "Labels and sorts new mail as it lands.",
  },
  {
    id: "t-meeting-prep",
    name: "Meeting prep",
    blurb: "Briefs you before each meeting.",
  },
  {
    id: "t-weekly-digest",
    name: "Weekly team digest",
    blurb: "Summarises the week every Friday.",
  },
  {
    id: "t-quiet-nudge",
    name: "Quiet-contact nudge",
    blurb: "Flags key people who’ve gone quiet.",
  },
];
